'use client';

import React, { useEffect, useRef, useState } from 'react';
import dynamic from 'next/dynamic';
import { useSearchParams } from 'next/navigation';
import { ChevronDown, SlidersHorizontal } from 'lucide-react';
import { propertyTypeLabel } from '@/lib/property-types';
import { formatPrice, numberFormat } from '@/lib/format';
import { readFilters, writeFilters, activeCount, type FilterValues } from '@/lib/filter-state';
import { useResultsNavigation } from './use-results-navigation';
import { preloadFilterPanel } from './LazyFilterPanel';

// The main filters as buttons over the list, on a wide screen (listing browse
// plan, step 3): Price, Beds & baths, Type, Seller - each opens under itself
// and applies as soon as something is chosen - and "All filters" for the
// full panel. A button that has a filter says what it is ("Up to $500K").

// The sections inside the buttons load when a button is about to be used -
// hover, focus or touch - not with every list (speed, 2026-09-25). The full
// panel loads the same file, so either one warms the other.
const loadSections = () => import('./FilterSections');
let sectionsLoading: Promise<unknown> | null = null;
const preloadSections = () => {
  sectionsLoading ??= loadSections();
};
const waiting = () => <div role="status" className="h-24 rounded-xl bg-neutral-800/40 animate-pulse motion-reduce:animate-none" />;
const PriceSection = dynamic(() => loadSections().then((m) => m.PriceSection), { ssr: false, loading: waiting });
const RoomsSection = dynamic(() => loadSections().then((m) => m.RoomsSection), { ssr: false, loading: waiting });
const TypeSection = dynamic(() => loadSections().then((m) => m.TypeSection), { ssr: false, loading: waiting });
const SellerSection = dynamic(() => loadSections().then((m) => m.SellerSection), { ssr: false, loading: waiting });

const buttonBase =
  'h-11 flex items-center gap-1.5 px-3 rounded-xl border text-sm font-semibold whitespace-nowrap cursor-pointer';
const idle = 'border-neutral-800 bg-neutral-900 text-neutral-100';
const set = 'border-white bg-white text-black';

function compactPrice(value: string, lang: string, rent = false): string {
  // A monthly rent is short already, and "$2K" would hide $1,750 from $2,250.
  if (rent) return formatPrice(value, lang);
  const n = Number(value);
  const fr = lang === 'fr';
  if (n >= 1_000_000) {
    const m = numberFormat(fr ? 'fr-CA' : 'en-CA', { maximumFractionDigits: 2 }).format(n / 1_000_000);
    return fr ? `${m} M$` : `$${m}M`;
  }
  const k = Math.round(n / 1000);
  return fr ? `${k} k$` : `$${k}K`;
}

function Popover({ label, active, children, alignRight = false }: { label: string; active: boolean; children: React.ReactNode; alignRight?: boolean }) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  const trigger = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    if (!open) return;
    const outside = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    const escape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        setOpen(false);
        trigger.current?.focus();
      }
    };
    document.addEventListener('mousedown', outside);
    document.addEventListener('keydown', escape);
    return () => {
      document.removeEventListener('mousedown', outside);
      document.removeEventListener('keydown', escape);
    };
  }, [open]);

  return (
    <div className="relative" ref={ref}>
      <button
        ref={trigger}
        type="button"
        aria-expanded={open}
        onClick={() => setOpen((o) => !o)}
        onMouseEnter={preloadSections}
        onFocus={preloadSections}
        onTouchStart={preloadSections}
        className={`${buttonBase} ${active ? set : idle}`}
      >
        <span>{label}</span>
        <ChevronDown aria-hidden="true" className={`w-4 h-4 transition-transform motion-reduce:transition-none ${open ? 'rotate-180' : ''}`} />
      </button>
      {open && (
        <div className={`absolute top-full mt-2 ${alignRight ? 'right-0' : 'left-0'} z-40 w-[22rem] p-4 rounded-2xl border border-neutral-800 bg-neutral-900 shadow-2xl`}>
          {children}
        </div>
      )}
    </div>
  );
}

export default function QuickFilters({ lang, onOpenAll, rent = false }: { lang: string; onOpenAll: () => void; rent?: boolean }) {
  const isFr = lang === 'fr';
  const searchParams = useSearchParams();
  const go = useResultsNavigation();
  const current = new URLSearchParams(searchParams?.toString() || '');
  const values = readFilters(current);

  // Each choice applies at once: a wide screen shows the list change beside it.
  const change = (patch: Partial<FilterValues>) => go(writeFilters(current, { ...values, ...patch }));

  const priceLabel = values.minPrice && values.maxPrice
    ? `${compactPrice(values.minPrice, lang, rent)} – ${compactPrice(values.maxPrice, lang, rent)}`
    : values.maxPrice
      ? `${isFr ? 'Jusqu’à' : 'Up to'} ${compactPrice(values.maxPrice, lang, rent)}`
      : values.minPrice
        ? `${isFr ? 'Dès' : 'From'} ${compactPrice(values.minPrice, lang, rent)}`
        : rent ? (isFr ? 'Loyer' : 'Rent') : (isFr ? 'Prix' : 'Price');
  const rooms = [
    values.bedrooms ? `${values.bedrooms}+ ${isFr ? 'ch.' : 'bd'}` : '',
    values.bathrooms ? `${values.bathrooms}+ ${isFr ? 'sdb' : 'ba'}` : '',
  ].filter(Boolean).join(', ');
  const types = values.propertyType;
  const typeLabel = types.length === 0
    ? (isFr ? 'Type' : 'Type')
    : types.length === 1 ? propertyTypeLabel(types[0], lang) : `${propertyTypeLabel(types[0], lang)} +${types.length - 1}`;
  const sellerLabel = values.ownerType === 'private' ? (isFr ? 'Particuliers' : 'Private sellers')
    : values.ownerType === 'agency' ? (isFr ? 'Agences' : 'Agencies')
      : (isFr ? 'Vendeur' : 'Seller');
  const all = activeCount(values);

  return (
    <div className="flex items-center gap-2">
      <Popover label={priceLabel} active={!!(values.minPrice || values.maxPrice)}>
        <PriceSection values={values} onChange={change} lang={lang} rent={rent} />
      </Popover>
      <Popover label={rooms || (isFr ? 'Chambres et sdb' : 'Beds & baths')} active={!!rooms}>
        <RoomsSection values={values} onChange={change} lang={lang} />
      </Popover>
      <Popover label={typeLabel} active={types.length > 0}>
        <TypeSection values={values} onChange={change} lang={lang} />
      </Popover>
      <Popover label={sellerLabel} active={!!values.ownerType}>
        <SellerSection values={values} onChange={change} lang={lang} />
      </Popover>
      <button
        type="button"
        onClick={onOpenAll}
        onMouseEnter={preloadFilterPanel}
        onFocus={preloadFilterPanel}
        aria-haspopup="dialog"
        className={`${buttonBase} ${idle}`}
      >
        <SlidersHorizontal className="w-4 h-4" />
        <span>{isFr ? 'Tous les filtres' : 'All filters'}</span>
        {all > 0 && (
          <span className="min-w-5 h-5 px-1 rounded-full bg-gold text-black text-xs font-bold flex items-center justify-center">{all}</span>
        )}
      </button>
    </div>
  );
}
